import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ModuleLayout from '@/components/admin/ModuleLayout';
import Alert from '@/components/ui/alert/Alert';
import { Toggle } from '@/components/admin/FormComponents';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { LoadState } from '@/components/ui/LoadState';
import { toastError, toastSuccess } from '@/lib/toast';
import { getValidationErrorMessage } from '@/lib/errors';
import { cccdAdmin, type EventSponsor, type SponsorTier } from '@/lib/api/cccdAdminClient';
import { TIER_LABEL } from './SponsorsPage';

const LOGO_TYPES = ['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const inputCls =
  'w-full h-11 rounded-[10px] border border-gray-300 px-3.5 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';
const labelCls = 'mb-1.5 block text-[13px] font-medium text-gray-600 dark:text-gray-400';

type FormState = { name: string; url: string; tier: SponsorTier; sortOrder: string; isActive: boolean };
const emptyForm: FormState = { name: '', url: '', tier: 'aliado', sortOrder: '0', isActive: true };

/** El logo se valida en el navegador antes de pedir la URL de subida. */
function logoProblem(file: File): string | null {
  if (!LOGO_TYPES.includes(file.type)) return 'El logo debe ser PNG, SVG, WebP o JPG.';
  if (file.size > MAX_LOGO_BYTES) return 'El logo pesa más de 2 MB.';
  return null;
}

// Misma página para crear y editar un patrocinador (sin diálogos). El logo se sube
// después de guardar los datos, porque la URL de subida necesita el id del patrocinador.
export default function SponsorFormPage() {
  const { eventId: nationalId, regionalId, sponsorId } = useParams<{ eventId?: string; regionalId?: string; sponsorId?: string }>();
  const isRegional = Boolean(regionalId);
  const eventId = (nationalId ?? regionalId)!;
  const base = isRegional ? `/cccd/regionals/${eventId}` : `/cccd/events/${eventId}`;
  const isEdit = Boolean(sponsorId);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ['event-sponsors', eventId],
    queryFn: () => cccdAdmin.listSponsors(eventId),
    enabled: isEdit,
  });
  const existing: EventSponsor | undefined = sponsors.find((s) => s.id === sponsorId);

  useEffect(() => {
    if (!existing) return;
    setForm({
      name: existing.name,
      url: existing.url ?? '',
      tier: existing.tier,
      sortOrder: String(existing.sortOrder),
      isActive: existing.isActive,
    });
  }, [existing]);

  // Libera la URL temporal de la vista previa al cambiar de archivo o salir.
  useEffect(() => {
    if (!logoFile) return;
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const backToList = () => navigate(`${base}/sponsors`);

  function onLogoChosen(file: File | undefined) {
    if (!file) return;
    const problem = logoProblem(file);
    setLogoError(problem ?? '');
    setLogoFile(problem ? null : file);
  }

  async function uploadLogo(id: string, file: File) {
    const { upload_url, key } = await cccdAdmin.getSponsorLogoUploadUrl(id, { filename: file.name, content_type: file.type });
    const put = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    if (!put.ok) throw new Error(`No se pudo subir el logo (${put.status}).`);
    await cccdAdmin.updateSponsor(id, { logoKey: key });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');

    const url = form.url.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      setError('El enlace debe empezar por http:// o https://');
      return;
    }

    setSaving(true);
    const fields = { name: form.name.trim(), url: url || null, tier: form.tier, sortOrder: Number(form.sortOrder) || 0 };
    let savedId = sponsorId;
    try {
      if (isEdit) {
        await cccdAdmin.updateSponsor(sponsorId!, { ...fields, isActive: form.isActive });
      } else {
        savedId = (await cccdAdmin.createSponsor(eventId, fields)).id;
      }
    } catch (err: unknown) {
      const message = getValidationErrorMessage(err);
      setError(message);
      toastError(message);
      setSaving(false);
      return;
    }

    if (logoFile && savedId) {
      try {
        await uploadLogo(savedId, logoFile);
      } catch (err: unknown) {
        // El patrocinador ya existe: seguir en "crear" lo duplicaría. Se pasa a editarlo para reintentar solo el logo.
        toastError(`Se guardaron los datos, pero el logo no se subió: ${getValidationErrorMessage(err)} Vuelve a elegir el archivo.`);
        await qc.invalidateQueries({ queryKey: ['event-sponsors', eventId] });
        setSaving(false);
        navigate(`${base}/sponsors/${savedId}/edit`);
        return;
      }
    }

    await qc.invalidateQueries({ queryKey: ['event-sponsors', eventId] });
    toastSuccess(isEdit ? 'Patrocinador actualizado.' : 'Patrocinador agregado.');
    backToList();
  }

  const shownLogo = logoPreview ?? existing?.logoUrl ?? null;

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: 'SateLab', to: '/' },
        { label: 'CCCD', to: '/cccd' },
        { label: isRegional ? 'Regionales' : 'Nacionales', to: base },
        { label: 'Patrocinadores', to: `${base}/sponsors` },
        isEdit ? 'Editar' : 'Nuevo',
      ]}
      title={isEdit ? existing?.name || 'Editar patrocinador' : 'Nuevo patrocinador'}
      subtitle="Se muestra en las pantallas públicas del evento"
      tabs={[]}
      metrics={[]}
      actions={
        <>
          {isEdit && <Toggle checked={form.isActive} onChange={(checked) => set('isActive', checked)} label="Activo" />}
          <button
            type="button"
            onClick={backToList}
            className="min-h-11 rounded-[10px] border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="sponsor-form"
            disabled={saving}
            className="min-h-11 rounded-[10px] bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Agregar patrocinador'}
          </button>
        </>
      }
    >
      {isEdit && isLoading ? (
        <LoadState />
      ) : (
        <form id="sponsor-form" onSubmit={onSubmit} className="flex max-w-3xl flex-col gap-5">
          <SectionHeader
            title="Datos"
            subtitle="Cómo se identifica y dónde enlaza el patrocinador"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="sponsor-name" className={labelCls}>
                  Nombre <span className="text-error-500">*</span>
                </label>
                <input id="sponsor-name" required maxLength={120} value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sponsor-url" className={labelCls}>Enlace (opcional)</label>
                <input
                  id="sponsor-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://"
                  value={form.url}
                  onChange={(e) => set('url', e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="sponsor-tier" className={labelCls}>Nivel</label>
                <select id="sponsor-tier" value={form.tier} onChange={(e) => set('tier', e.target.value as SponsorTier)} className={inputCls}>
                  {(Object.keys(TIER_LABEL) as SponsorTier[]).map((tier) => (
                    <option key={tier} value={tier}>{TIER_LABEL[tier]}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Principal: fijo y más grande. Aliado y apoyo: rotan en la franja.
                </p>
              </div>
              <div>
                <label htmlFor="sponsor-order" className={labelCls}>Orden dentro del nivel</label>
                <input id="sponsor-order" type="number" min={0} max={10000} value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} className={inputCls} />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Menor número aparece primero. También puedes reordenar desde la lista.</p>
              </div>
            </div>
          </SectionHeader>

          <SectionHeader
            title="Logo"
            subtitle="PNG, SVG, WebP o JPG, hasta 2 MB. Mejor con fondo transparente"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" /><circle cx="9" cy="10" r="1.6" stroke="currentColor" strokeWidth="1.6" /><path d="M4 17l5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>}
          >
            <div className="flex flex-wrap items-center gap-5">
              {/* Fondo blanco: es el mismo en que se verá el logo en pantalla. */}
              <div className="flex h-24 w-48 items-center justify-center rounded-xl border border-gray-200 bg-white p-3">
                {shownLogo ? (
                  <img src={shownLogo} alt="Vista previa del logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-xs text-gray-400">Sin logo</span>
                )}
              </div>
              <div>
                <label className="inline-flex min-h-11 cursor-pointer items-center rounded-[10px] border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                  <input
                    type="file"
                    accept={LOGO_TYPES.join(',')}
                    className="sr-only"
                    onChange={(e) => {
                      onLogoChosen(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  {shownLogo ? 'Cambiar logo' : 'Elegir logo'}
                </label>
                {logoFile && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{logoFile.name} se subirá al guardar.</p>}
                {logoError && (
                  <p role="alert" className="mt-2 text-xs text-error-500">{logoError}</p>
                )}
              </div>
            </div>
            {!shownLogo && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Sin logo el patrocinador no se muestra en pantalla.</p>
            )}
          </SectionHeader>

          {error && <Alert variant="error" title="No se pudo guardar" message={error} />}
        </form>
      )}
    </ModuleLayout>
  );
}
